import React from 'react';
import { Text } from '@/hen-ui/atomics';
import { cn } from '@/hen-ui/utils';

/**
 * Props for the Brand component
 *
 * Brand is a sub-component of Footer that displays the company logo and tagline.
 * It uses the Text atomic component for consistent typography.
 */
type BrandProps = {
  /** Logo element to display (can be an image, SVG, or any React node) */
  logo: React.ReactNode;
  /** Tagline text to display below the logo (rendered using Text atomic with b4 variant) */
  tagline: string;
  /** Class name to apply to the brand section */
  className?: string;
};

/**
 * Brand - Displays the company/brand logo and tagline in the footer
 *
 * This sub-component is part of the Footer compound component pattern.
 * It provides a consistent way to display brand identity in footers.
 * Responsive: full width on mobile, fit-content on medium screens and above.
 * Tagline text wraps properly with responsive max-width constraints.
 *
 * @param logo - The logo element to display (Image, SVG, or any React node)
 * @param tagline - The tagline text displayed below the logo (via Text atomic)
 */
const Brand: React.FC<BrandProps> = ({ logo, tagline, className }) => (
  <section
    className={cn(
      'flex md:min-w-[311px] gap-lg flex-col w-full md:w-fit',
      className,
    )}
  >
    {logo}
    <Text
      className='max-w-full md:max-w-[400px] break-words'
      variant='b4'
      color='white'
    >
      {tagline}
    </Text>
  </section>
);
Brand.displayName = 'Footer.Brand';

/**
 * SectionGroup - Container for grouping multiple Section components together
 *
 * This sub-component provides flexible layout with minimum width and spacing
 * between sections. Sections wrap on smaller screens and display horizontally
 * on larger screens. Uses justify-between spacing at xl breakpoint and above.
 *
 * @param children - Section components to be grouped together (Footer.Section elements)
 * @param className - Class name to apply to the section group section
 */
type SectionGroupProps = React.PropsWithChildren<{
  /** Class name to apply to the section group section */
  className?: string;
}>;

const SectionGroup: React.FC<SectionGroupProps> = ({ children, className }) => (
  <section
    className={cn(
      'flex-[1_1_0] flex min-w-[311px] xl:justify-between items-start gap-2xl flex-wrap',
      className,
    )}
  >
    {children}
  </section>
);
SectionGroup.displayName = 'Footer.SectionGroup';

/**
 * Props for the Section component
 *
 * Section is a sub-component that creates a titled section within the footer,
 * typically used to organize navigation links or information.
 */
type SectionProps = React.PropsWithChildren<{
  /** Title text for the section (e.g., "Company", "Resources", "Legal") */
  title: string;
  /** Class name to apply to the section */
  className?: string;
}>;

/**
 * Section - A footer section with a title and content
 *
 * This sub-component creates a titled section within the footer, typically used
 * to organize related links or information. Use within Footer.SectionGroup to
 * create multiple columns of navigation. Responsive: full width on mobile,
 * flex behavior on medium screens and above to allow wrapping.
 *
 * @param title - The section title displayed as bold text
 * @param children - Content to display below the title (typically Link components)
 */
const Section: React.FC<SectionProps> = ({ title, children, className }) => (
  <div
    className={cn(
      'gap-sm md:gap-xs flex flex-col w-full md:flex-[1_1_0]',
      className,
    )}
  >
    <Text variant='b5' className='font-bold' color='white'>
      {title}
    </Text>
    {children}
  </div>
);
Section.displayName = 'Footer.Section';

/**
 * Links - Container for displaying navigation links in a flexible layout
 *
 * This sub-component provides a horizontal layout for links, typically used
 * in the Footer.Bottom section for legal links, social media, or other
 * secondary navigation.
 *
 * @param children - Link elements to display (typically Link atomic components or anchor tags)
 * @param className - Class name to apply to the links section
 */
type LinksProps = React.PropsWithChildren<{
  /** Class name to apply to the links section */
  className?: string;
}>;

const Links: React.FC<LinksProps> = ({ children, className }) => (
  <div
    className={cn(
      'flex justify-between items-start gap-x-xl self-stretch flex-wrap',
      className,
    )}
  >
    {children}
  </div>
);
Links.displayName = 'Footer.Links';

/**
 * Top - Container for the top section of the footer
 *
 * This sub-component provides the main content area of the footer with
 * flexible layout and maximum desktop width constraint. Typically contains
 * Brand and SectionGroup components.
 *
 * @param children - Content for the top section (usually Footer.Brand and Footer.SectionGroup)
 * @param className - Class name to apply to the top section
 */
type TopProps = React.PropsWithChildren<{
  /** Class name to apply to the top section */
  className?: string;
}>;

const Top: React.FC<TopProps> = ({ children, className }) => (
  <div
    className={cn(
      'self-stretch flex-wrap items-start justify-start flex max-w-desktop gap-y-xl gap-x-max',
      className,
    )}
  >
    {children}
  </div>
);
Top.displayName = 'Footer.Top';

/**
 * Bottom - Container for the bottom section of the footer
 *
 * This sub-component provides the bottom section of the footer with a horizontal
 * divider line and flexible content layout. Typically used for copyright information,
 * legal links, and social media icons.
 *
 * @param children - Content for the bottom section (typically Text for copyright and Footer.Links for legal links)
 * @param className - Class name to apply to the bottom section
 */
type BottomProps = React.PropsWithChildren<{
  /** Class name to apply to the bottom section */
  className?: string;
}>;

const Bottom: React.FC<BottomProps> = ({ children, className }) => (
  <>
    <div className='bg-neutral-500 w-full h-[1px]' />
    <div
      className={cn(
        'flex justify-between items-start gap-x-xl self-stretch flex-wrap',
        className,
      )}
    >
      {children}
    </div>
  </>
);
Bottom.displayName = 'Footer.Bottom';

/**
 * FooterComponent interface - Defines the compound component structure for Footer
 *
 * This interface extends the main Footer component with sub-components, enabling
 * the compound component pattern. This allows flexible composition while maintaining
 * a consistent API.
 */
type FooterComponentProps = React.PropsWithChildren<{
  /** Class name to apply to the footer */
  className?: string;
}>;
interface FooterComponent extends React.FC<FooterComponentProps> {
  /** Brand sub-component for displaying logo and tagline */
  Brand: React.FC<BrandProps>;
  /** SectionGroup sub-component for grouping multiple sections */
  SectionGroup: React.FC<SectionGroupProps>;
  /** Section sub-component for creating titled sections with links */
  Section: React.FC<SectionProps>;
  /** Links sub-component for horizontal link layouts */
  Links: React.FC<LinksProps>;
  /** Top sub-component for the main footer content area */
  Top: React.FC<TopProps>;
  /** Bottom sub-component for copyright and legal links */
  Bottom: React.FC<BottomProps>;
}

/**
 * Footer - A compound component for building flexible, accessible website footers
 *
 * This composite component follows the compound component pattern, providing
 * sub-components that can be composed together to create various footer layouts
 * while maintaining design consistency. It is composed entirely of atomic components
 * (Text) and uses HEN Design System tokens for all styling.
 *
 * ## Component Composition
 * - Uses `Text` atomic component for tagline and copyright text
 * - All styling uses design tokens from tailwind.config.ts
 * - Semantic HTML with `<footer>` element for accessibility
 *
 * ## Sub-Components
 * - **Footer.Top**: Main content area (brand + navigation sections)
 * - **Footer.Bottom**: Bottom section (copyright + legal links)
 * - **Footer.Brand**: Logo and tagline display
 * - **Footer.SectionGroup**: Groups multiple sections horizontally
 * - **Footer.Section**: Individual titled section with links
 * - **Footer.Links**: Horizontal layout for links
 *
 * ## Use Cases
 * - Full-featured website footers with navigation
 * - Minimal footers with just copyright
 * - Marketing site footers with social links
 * - Application footers with legal information
 *
 * ## Responsive Behavior
 * - Sections stack vertically on mobile and wrap on medium screens
 * - Sections display horizontally with justify-between spacing at xl breakpoint (1440px) and above
 * - Brand and tagline are full width on mobile, constrained on larger screens
 * - Images are constrained to max 200px width on mobile
 *
 * @example
 * ```tsx
 * <Footer>
 *   <Footer.Top>
 *     <Footer.Brand logo={<Logo />} tagline="Your trusted education partner" />
 *     <Footer.SectionGroup>
 *       <Footer.Section title="Company">
 *         <Link href="/about">About</Link>
 *         <Link href="/careers">Careers</Link>
 *       </Footer.Section>
 *     </Footer.SectionGroup>
 *   </Footer.Top>
 *   <Footer.Bottom>
 *     <Text variant="b5">© 2026 Company Name</Text>
 *     <Footer.Links>
 *       <Link href="/privacy">Privacy</Link>
 *       <Link href="/terms">Terms</Link>
 *     </Footer.Links>
 *   </Footer.Bottom>
 * </Footer>
 * ```
 *
 * @param children - Footer content composed using sub-components (Footer.Top, Footer.Bottom, etc.)
 */
export const Footer: FooterComponent = ({ children, className }) => {
  return (
    <footer
      className={cn(
        // TODO: need padding value for footer mobile
        'text-white flex-col py-16 px-8 md:p-footer gap-2xl bg-black items-center flex',
        className,
      )}
    >
      {children}
    </footer>
  );
};
Footer.displayName = 'Footer';

// Attach sub-components to Footer for compound component pattern
Footer.Brand = Brand;
Footer.SectionGroup = SectionGroup;
Footer.Section = Section;
Footer.Links = Links;
Footer.Bottom = Bottom;
Footer.Top = Top;
